import React, { useState } from 'react';
import { FiSend, FiX } from 'react-icons/fi';
import { FaPaperPlane } from 'react-icons/fa';
import styled from 'styled-components';
import api from '../../../../../services/api';
import { useModal } from '../../../../../hooks/Modal';
import { useToast } from '../../../../../hooks/Toast';
import { useAuth } from '../../../../../hooks/Auth';

// =========================================================
// Templates (mesmo espelho — desta vez como select)
// =========================================================
const TEMPLATES = [
  { value: 'reminder_before_due',         label: 'Lembrete pré-vencimento' },
  { value: 'reminder_after_due:1',        label: 'Lembrete pós-vencimento (1º envio)' },
  { value: 'reminder_after_due:2',        label: 'Lembrete pós-vencimento (2º envio)' },
  { value: 'collection_letter',           label: 'Carta de cobrança' },
  { value: 'credit_card_reminder',        label: 'Lembrete cartão recorrente' },
  { value: 'team_default_notice',         label: 'Notificação à equipe — inadimplência' },
  { value: 'team_regularization',         label: 'Notificação à equipe — regularização' }
];

// =========================================================
// Styled
// =========================================================
const Container = styled.div`
  background: #fff;
  border-radius: 10px;
  min-width: 380px;
  max-width: 480px;
  width: 100%;
  color: #1f2937;
  font-family: 'Inter', -apple-system, sans-serif;
`;
const Header = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: flex-start; gap: 12px;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p  { font-size: 12px; color: #6b7280; margin-top: 2px; }
  > div:first-child { flex: 1; }
  > button {
    background: none; border: none; cursor: pointer; color: #9ca3af;
    &:hover { color: #111827; }
  }
`;
const Body = styled.div` padding: 18px 22px; `;
const Field = styled.div` margin-bottom: 14px; `;
const Label = styled.label`
  display: block;
  font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px;
`;
const Hint = styled.div`
  font-size: 11px; color: #6b7280; margin-top: 4px;
`;
const Input = styled.input`
  width: 100%; height: 40px; padding: 0 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px; font-size: 13px;
  font-family: inherit; background: #fff;
  &:focus { outline: none; border-color: var(--adminColor); box-shadow: 0 0 0 3px rgba(111,33,118,.1); }
`;
const Select = styled.select`
  width: 100%; height: 40px; padding: 0 12px;
  border: 1.5px solid #e5e7eb; border-radius: 8px; font-size: 13px;
  font-family: inherit; background: #fff; cursor: pointer;
  &:focus { outline: none; border-color: var(--adminColor); }
`;
const Notice = styled.div`
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  border-radius: 8px; padding: 10px 12px; font-size: 11px; line-height: 1.5;
  margin-bottom: 14px;
`;
const Footer = styled.div`
  padding: 12px 22px; border-top: 1px solid #f3f4f6;
  display: flex; gap: 10px; justify-content: flex-end;
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 14px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff; color: #374151;
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #f9fafb; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border-radius: 8px;
  border: none; background: var(--adminColor); color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  &:hover { background: #5a1a60; }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;

// =========================================================
// Modal
// =========================================================
const TestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
  const [mailType, setMailType] = useState(TEMPLATES[0].value);
  const [email, setEmail] = useState('');
  const [invoiceHash, setInvoiceHash] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) {
      addToast({ title: 'Informe um e-mail de destino', type: 'error' });
      return;
    }
    setSending(true);
    try {
      // type pode vir como "reminder_after_due:2" → separa sequence
      const [type, seqStr] = mailType.split(':');
      const sequence = seqStr ? Number(seqStr) : undefined;
      await api.post(`/invoice-mail/test/${type}`, {
        email,
        invoice_hash: invoiceHash || undefined,
        sequence
      });
      addToast({ title: 'E-mail de teste agendado', type: 'success' });
      onClose();
    } catch (err) {
      addToast({ title: 'Erro ao agendar teste', type: 'error' });
      handleApiErrors(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <h3>Enviar e-mail de teste</h3>
          <p>Dispara um dos templates de fatura para o endereço informado.</p>
        </div>
        <button onClick={onClose}><FiX size={16} /></button>
      </Header>
      <Body>
        <Notice>
          O e-mail é enviado <strong>apenas ao destinatário informado</strong>. Aluno e equipe não recebem cópia.
          Os placeholders usam os dados de uma fatura real (a primeira encontrada, ou a do hash informado).
        </Notice>

        <Field>
          <Label>Template</Label>
          <Select value={mailType} onChange={e => setMailType(e.target.value)} disabled={sending}>
            {TEMPLATES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>E-mail de destino</Label>
          <Input
            type="email"
            placeholder="seu.email@exemplo.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={sending}
            autoFocus
          />
        </Field>

        <Field>
          <Label>Hash da fatura (opcional)</Label>
          <Input
            type="text"
            placeholder="Use uma fatura específica como referência…"
            value={invoiceHash}
            onChange={e => setInvoiceHash(e.target.value)}
            disabled={sending}
          />
          <Hint>Se vazio, o backend pega a primeira fatura disponível como exemplo.</Hint>
        </Field>
      </Body>
      <Footer>
        <BtnCancel onClick={onClose} disabled={sending}>Cancelar</BtnCancel>
        <BtnPrimary onClick={handleSend} disabled={sending || !email}>
          <FiSend size={13} />
          {sending ? 'Enviando…' : 'Enviar teste'}
        </BtnPrimary>
      </Footer>
    </Container>
  );
};

// =========================================================
// Botão (vai em headModules — sem lineData)
// =========================================================
export default function SendInvoiceTestMail(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'send-invoice-test-mail',
      theme: 'whiteModal',
      title: '',
      content: <TestModal onClose={() => removeModal('send-invoice-test-mail')} />
    });
  };

  return (
    <button
      className="searchButton"
      title="Enviar e-mail de teste"
      type="button"
      onClick={open}
    >
      <FaPaperPlane />
    </button>
  );
}
